import { useState } from "react";
import { X, Sparkles, Loader2, AlertTriangle, Route } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function AIPanel({ onClose }) {
  const { t } = useI18n();
  const [tab, setTab] = useState("errors");
  const [errors, setErrors] = useState(null);
  const [routeInput, setRouteInput] = useState("");
  const [route, setRoute] = useState(null);
  const [busy, setBusy] = useState(false);

  const detect = async () => {
    setBusy(true); setErrors(null);
    try { const { data } = await api.post("/ai/detect-errors"); setErrors(data); }
    finally { setBusy(false); }
  };

  const suggest = async () => {
    setBusy(true); setRoute(null);
    try { const { data } = await api.post("/ai/suggest-route", { prompt: routeInput }); setRoute(data); }
    finally { setBusy(false); }
  };

  const sevColor = { high: "text-red-400", medium: "text-yellow-400", low: "text-slate-400" };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="ai-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-gradient-to-r from-pink-900/30 to-slate-900">
        <div className="flex items-center gap-2"><Sparkles size={18} className="text-pink-400" /><h2 className="font-heading font-bold text-base">Asystent AI</h2></div>
        <button onClick={onClose} data-testid="ai-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
      </div>
      <div className="flex border-b border-slate-800">
        <button onClick={() => setTab("errors")} className={`flex-1 py-2 text-sm flex items-center justify-center gap-1.5 ${tab === "errors" ? "text-cyan-400 border-b-2 border-cyan-400" : "text-slate-400"}`}><AlertTriangle size={14} /> Błędy</button>
        <button onClick={() => setTab("route")} className={`flex-1 py-2 text-sm flex items-center justify-center gap-1.5 ${tab === "route" ? "text-cyan-400 border-b-2 border-cyan-400" : "text-slate-400"}`}><Route size={14} /> Trasa</button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === "errors" && (
          <div>
            <button onClick={detect} disabled={busy} data-testid="ai-detect-btn"
              className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60 mb-3">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Wykryj błędy w dokumentacji
            </button>
            {errors && (
              <div className="space-y-2">
                {errors.issues.length === 0 && <p className="text-sm text-green-400">Nie wykryto błędów regułowych.</p>}
                {errors.issues.map((iss, i) => (
                  <div key={i} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm">
                    <span className={`text-xs uppercase font-semibold ${sevColor[iss.severity]}`}>{iss.severity}</span>
                    <div className="text-slate-300">{iss.text}</div>
                  </div>
                ))}
                {errors.ai_summary && (
                  <div className="bg-pink-900/20 border border-pink-800/40 rounded px-3 py-2 text-sm text-slate-200 whitespace-pre-wrap mt-3">
                    <div className="text-xs text-pink-400 font-semibold mb-1">Analiza AI</div>
                    {errors.ai_summary}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {tab === "route" && (
          <div>
            <textarea value={routeInput} onChange={(e) => setRouteInput(e.target.value)} rows={3}
              placeholder={t("Opisz nowego klienta / lokalizację (np. ul. Nowa 5, blok 12 mieszkań)…")}
              data-testid="ai-route-input"
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm mb-2 outline-none focus:ring-1 focus:ring-cyan-500" />
            <button onClick={suggest} disabled={busy} data-testid="ai-suggest-btn"
              className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Route size={15} />} Zaproponuj trasę przyłącza
            </button>
            {route && (
              <div className="mt-3">
                <div className="bg-pink-900/20 border border-pink-800/40 rounded px-3 py-2 text-sm text-slate-200 whitespace-pre-wrap">
                  {route.suggestion}
                </div>
                <div className="text-xs text-slate-400 mt-2">Wolne włókna: {route.free_fibers.length}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
